/**
 * Save Form Data Action
 *
 * Assigner functions for updating context with form data from SUBMIT events.
 * Export plain functions here; the machine wraps them with `assign()` inside
 * `setup()` so XState's phantom types stay compatible (no `as any` needed).
 */

import type {
  InsuranceConnectorContext,
  InsuranceConnectorEvent,
} from "../types";

/**
 * Save form data to context — extracts data from SUBMIT events and merges it
 */
export const saveFormDataAssigner = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}): Partial<InsuranceConnectorContext> => {
  if (event.type !== "SUBMIT") {
    return {};
  }

  const data = event.data;
  const updates: Partial<InsuranceConnectorContext> = {};

  if (data.firstName !== undefined) {
    updates.firstName = data.firstName as string;
  }
  if (data.lastName !== undefined) {
    updates.lastName = data.lastName as string;
  }
  if (data.birthday !== undefined) {
    updates.birthday = data.birthday as string;
  }
  if (data.phoneNumber !== undefined) {
    updates.phoneNumber = data.phoneNumber as string;
  }
  if (data.consumerState !== undefined) {
    updates.consumerState = data.consumerState as string;
  }
  if (data.otp !== undefined) {
    updates.otp = data.otp as string;
  }

  if (
    data.payer !== undefined ||
    data.memberId !== undefined ||
    data.insuredState !== undefined
  ) {
    updates.primaryInsurance = {
      payer: (data.payer as string) || context.primaryInsurance?.payer || "",
      payerId: (data.payerId as string) || context.primaryInsurance?.payerId,
      memberId:
        (data.memberId as string) || context.primaryInsurance?.memberId || "",
      insuredState:
        (data.insuredState as string) ||
        context.primaryInsurance?.insuredState ||
        "",
    };
  }

  if (
    data.secondaryPayer !== undefined ||
    data.secondaryMemberId !== undefined ||
    data.secondaryInsuredState !== undefined
  ) {
    updates.secondaryInsurance = {
      payer:
        (data.secondaryPayer as string) ||
        context.secondaryInsurance?.payer ||
        "",
      payerId:
        (data.secondaryPayerId as string) ||
        context.secondaryInsurance?.payerId,
      memberId:
        (data.secondaryMemberId as string) ||
        context.secondaryInsurance?.memberId ||
        "",
      insuredState:
        (data.secondaryInsuredState as string) ||
        context.secondaryInsurance?.insuredState ||
        "",
    };
  }

  return updates;
};

/**
 * Save return user data to context
 */
export const saveReturnUserDataAssigner = ({
  event,
}: {
  event: InsuranceConnectorEvent;
}): Partial<InsuranceConnectorContext> => {
  if (event.type !== "SUBMIT") {
    return {};
  }

  const data = event.data;

  return {
    isReturnUser: data.isReturnUser as boolean,
    consumerId: data.consumerId as string,
    userId: data.userId as string,
  };
};
